/**
 * useReconciliationConsumer
 *
 * Polls portal_reconciliation_outbox for unconsumed reconciliation events
 * and mirrors them into canonical job state.
 *
 * Flow:
 *   opportunity_accepted:
 *     → accept local MarketplaceOffer (via useMarketplaceOffers)
 *     → create scheduling_events row (schedule_set event)
 *     → mark outbox row consumed
 *
 *   execution_submitted_for_review:
 *     → advance jobs.current_stage to "QA Review"
 *     → set metadata.qaSubmitted=true, metadata.installStatus="Install Complete"
 *     → mark outbox row consumed
 *
 * Design constraints:
 *   - useRef guard prevents concurrent polling runs.
 *   - scheduling_events insert uses direct supabase write (UUID path) because
 *     JobOperationsService.createSchedulingEvent() resolves by jobNumber, but
 *     the outbox carries app_job_id (UUID directly).
 *   - org_id sourced from getCurrentOrganizationId() on every write.
 */

import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { useMarketplaceOffers } from "@/hooks/useMarketplaceOffers";
import {
  RECONCILIATION_INITIAL_POLL_DELAY_MS,
  RECONCILIATION_POLL_MS,
  SUPABASE_SYNC_DEBUG,
} from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";

const RECONCILIATION_BATCH_LIMIT = 5;
const MIRROR_SWEEP_INTERVAL_MS = 5 * 60_000;
const MIRROR_SWEEP_LIMIT = 25;
const CONSUMER_ID = "ccc_reconciliation_consumer";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface OutboxRow {
  id: string;
  kind: string;
  opportunity_id: string;
  organization_id: string;
  app_job_id: string;
  app_offer_id: string | null;
  actor_user_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  consumed_at: string | null;
  consumed_by: string | null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

export function useReconciliationConsumer(): void {
  const { offers, accept } = useMarketplaceOffers();
  // Stable ref so the interval closure always sees the latest offers/accept
  // without re-registering the interval on every render.
  const stateRef = useRef({ offers, accept });
  useEffect(() => {
    stateRef.current = { offers, accept };
  });

  const runningRef = useRef(false);
  const lastMirrorSweepAtRef = useRef(0);

  const maybeRunMirrorSweep = async () => {
    const now = Date.now();
    if (now - lastMirrorSweepAtRef.current < MIRROR_SWEEP_INTERVAL_MS) return;
    lastMirrorSweepAtRef.current = now;
    await reconcileAcceptedOppMirrors(MIRROR_SWEEP_LIMIT);
  };

  const poll = async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const { data, error } = await supabase
        .from("portal_reconciliation_outbox")
        .select("*")
        .is("consumed_at", null)
        .in("kind", ["opportunity_accepted", "execution_submitted_for_review"])
        .order("created_at", { ascending: true })
        .limit(RECONCILIATION_BATCH_LIMIT);

      // PGRST205 = table not yet in schema cache; silently skip until migration applied.
      if (error) {
        const errCode =
          typeof error === "object" &&
          error !== null &&
          "code" in error &&
          typeof (error as { code?: unknown }).code === "string"
            ? ((error as { code?: string }).code ?? "")
            : "";
        if (errCode !== "PGRST205") {
          console.warn("[useReconciliationConsumer] query error:", error);
        }
        return;
      }
      if (!data || data.length === 0) {
        await maybeRunMirrorSweep();
        return;
      }

      if (import.meta.env.DEV) {
        console.info("[useReconciliationConsumer] processing outbox batch", {
          count: data.length,
          limit: RECONCILIATION_BATCH_LIMIT,
        });
      }
      recordRealtimeMetric({
        source: "reconciliation-consumer",
        event: "exact-refetch",
        table: "portal_reconciliation_outbox",
        detail: SUPABASE_SYNC_DEBUG ? { count: data.length } : undefined,
      });

      for (const row of data as OutboxRow[]) {
        try {
          if (row.kind === "execution_submitted_for_review") {
            await processExecutionSubmittedRow(row);
          } else {
            await processRow(row, stateRef.current.offers, stateRef.current.accept);
          }
        } catch (err) {
          console.warn("[useReconciliationConsumer] row processing failed:", row.id, err);
        }
      }
    } catch (err) {
      console.warn("[useReconciliationConsumer] poll error:", err);
    } finally {
      runningRef.current = false;
    }
  };

  useEffect(() => {
    // Keep reconciliation off the route's critical boot path. The old immediate
    // poll could consume an outbox batch and write jobs before the first screen settled.
    let intervalTimer: ReturnType<typeof setInterval> | undefined;
    const firstPollTimer = setTimeout(() => {
      void poll();
      intervalTimer = setInterval(() => void poll(), RECONCILIATION_POLL_MS);
    }, RECONCILIATION_INITIAL_POLL_DELAY_MS);
    return () => {
      clearTimeout(firstPollTimer);
      if (intervalTimer) clearInterval(intervalTimer);
    };
     
  }, []);
}

async function processExecutionSubmittedRow(row: OutboxRow): Promise<void> {
  const orgId = row.organization_id || await getCurrentOrganizationId();
  if (!orgId) return;

  const payload = isRecord(row.payload) ? row.payload : {};
  const submittedAt =
    typeof payload.submitted_at === "string" && payload.submitted_at
      ? payload.submitted_at
      : row.created_at;

  // Resolve job by UUID app_job_id first, then by job_number fallback for legacy rows.
  let byUuidData: { id: string; metadata: unknown } | null = null;
  if (UUID_RE.test(row.app_job_id)) {
    const byUuid = await supabase
      .from("jobs")
      .select("id, metadata")
      .eq("organization_id", orgId)
      .eq("id", row.app_job_id)
      .maybeSingle();

    if (byUuid.error) {
      console.warn("[useReconciliationConsumer] execution job lookup by UUID failed:", row.id, byUuid.error);
      return;
    }
    byUuidData = byUuid.data as { id: string; metadata: unknown } | null;
  }

  let targetJob = byUuidData;
  if (!targetJob) {
    const byJobNumber = await supabase
      .from("jobs")
      .select("id, metadata")
      .eq("organization_id", orgId)
      .eq("job_number", row.app_job_id)
      .maybeSingle();

    if (byJobNumber.error) {
      console.warn("[useReconciliationConsumer] execution job lookup by job_number failed:", row.id, byJobNumber.error);
      return;
    }
    targetJob = byJobNumber.data as { id: string; metadata: unknown } | null;
  }

  if (!targetJob) {
    console.warn("[useReconciliationConsumer] no job found for execution submit row:", row.id, row.app_job_id);
    return;
  }

  const currentMeta = isRecord(targetJob.metadata) ? targetJob.metadata : {};
  const nextMeta = {
    ...currentMeta,
    qaSubmitted: true,
    qaStatus: "Submitted",
    installStatus: "Install Complete",
    lastActivityDate: submittedAt,
    currentStage: "QA Review",
  };

  const { error: jobUpdateErr } = await supabase
    .from("jobs")
    .update({
      current_stage: "QA Review",
      metadata: nextMeta,
      updated_at: new Date().toISOString(),
    })
    .eq("organization_id", orgId)
    .eq("id", targetJob.id);

  if (jobUpdateErr) {
    console.warn("[useReconciliationConsumer] execution lifecycle update failed:", row.id, jobUpdateErr);
    return;
  }

  const { error: consumeErr } = await supabase
    .from("portal_reconciliation_outbox")
    .update({
      consumed_at: new Date().toISOString(),
      consumed_by: CONSUMER_ID,
    })
    .eq("id", row.id);

  if (consumeErr) {
    console.warn("[useReconciliationConsumer] failed to mark execution row consumed:", row.id, consumeErr);
  }
}

async function processRow(
  row: OutboxRow,
  offers: ReturnType<typeof useMarketplaceOffers>["offers"],
  accept: ReturnType<typeof useMarketplaceOffers>["accept"],
): Promise<void> {
  const payloadContractorId =
    row.payload && typeof row.payload === "object" && typeof row.payload.contractor_id === "string"
      ? row.payload.contractor_id
      : null;

  // a. Find matching offer with strongest key first.
  const matchingOffer =
    (row.app_offer_id
      ? offers.find((o) => o.id === row.app_offer_id && o.status === "pending")
      : undefined) ??
    (payloadContractorId
      ? offers.find(
          (o) =>
            o.jobId === row.app_job_id &&
            o.contractorId === payloadContractorId &&
            o.status === "pending",
        )
      : undefined) ??
    offers.find((o) => o.jobId === row.app_job_id && o.status === "pending");

  let reconciled = false;
  if (matchingOffer) {
    // b. Accept the local offer through the canonical engine path.
    const result = accept(matchingOffer.id, CONSUMER_ID);
    reconciled = result.ok;
  }

  // If local state is stale but DB already reflects acceptance, treat the row
  // as reconciled so we can consume the outbox entry idempotently.
  if (!reconciled && row.app_offer_id) {
    const { data: acceptedOffer, error: acceptedLookupErr } = await supabase
      .from("marketplace_offers")
      .select("id")
      .eq("id", row.app_offer_id)
      .eq("status", "accepted")
      .maybeSingle();
    if (acceptedLookupErr) {
      console.warn("[useReconciliationConsumer] accepted lookup failed:", row.id, acceptedLookupErr);
    } else if (acceptedOffer) {
      reconciled = true;
    }
  }

  // Fallback: local state may be stale or this page may not have loaded offers yet.
  // Reconcile directly in DB by strongest keys so the UI can hydrate correct status.
  if (!reconciled) {
    if (row.app_offer_id) {
      const { data: updatedRows, error: dbErr } = await supabase
        .from("marketplace_offers")
        .update({
          status: "accepted",
          responded_at: row.created_at,
        })
        .eq("id", row.app_offer_id)
        .eq("status", "pending")
        .select("id");
      if (dbErr) {
        console.warn("[useReconciliationConsumer] direct offer reconcile failed:", row.id, dbErr);
      } else {
        reconciled = Array.isArray(updatedRows) && updatedRows.length > 0;
      }
    }
    if (!reconciled && payloadContractorId) {
      const { data: updatedRows, error: dbErr } = await supabase
        .from("marketplace_offers")
        .update({
          status: "accepted",
          responded_at: row.created_at,
        })
        .eq("job_id", row.app_job_id)
        .eq("contractor_id", payloadContractorId)
        .eq("status", "pending")
        .select("id");
      if (dbErr) {
        console.warn("[useReconciliationConsumer] direct job+contractor reconcile failed:", row.id, dbErr);
      } else {
        reconciled = Array.isArray(updatedRows) && updatedRows.length > 0;
      }
    }
  }

  if (!reconciled) {
    console.warn("[useReconciliationConsumer] no matching pending offer yet; leaving row unconsumed:", row.id);
    return;
  }

  // c. Insert a scheduling_events row for the acceptance signal.
  //    Uses direct supabase write because the outbox carries a job UUID,
  //    not a jobNumber string (which createSchedulingEvent() requires).
  if (row.organization_id) {
    const { error: seErr } = await supabase.from("scheduling_events").insert({
      organization_id: row.organization_id,
      job_id: row.app_job_id,
      contractor_id: null,
      event_type: "schedule_set",
      start_at: row.created_at,
      end_at: null,
      status: "pending",
      notes: `Reconciled from portal_reconciliation_outbox row ${row.id}`,
      created_by: CONSUMER_ID,
      brief_snapshot: {
        source: "reconciliation_consumer",
        opportunity_id: row.opportunity_id,
        app_offer_id: row.app_offer_id,
        actor_user_id: row.actor_user_id,
        payload: row.payload,
      },
    });
    if (seErr) {
      console.warn("[useReconciliationConsumer] scheduling_events insert failed:", seErr);
    }
  }

  // d. Mark the outbox row consumed only after a successful reconciliation.
  const { error: consumeErr } = await supabase
    .from("portal_reconciliation_outbox")
    .update({
      consumed_at: new Date().toISOString(),
      consumed_by: CONSUMER_ID,
    })
    .eq("id", row.id);

  if (consumeErr) {
    console.warn("[useReconciliationConsumer] failed to mark row consumed:", row.id, consumeErr);
  }
}

async function reconcileAcceptedOppMirrors(limit = MIRROR_SWEEP_LIMIT): Promise<void> {
  const orgId = await getCurrentOrganizationId();
  if (!orgId) return;

  const { data: acceptedRows, error: acceptedErr } = await supabase
    .from("installer_opportunities")
    .select("id, app_offer_id, app_job_id, contractor_id, decided_at, status, organization_id")
    .eq("organization_id", orgId)
    .eq("status", "accepted")
    .order("decided_at", { ascending: false, nullsFirst: false })
    .limit(limit);

  if (acceptedErr) {
    console.warn("[useReconciliationConsumer] accepted mirror query failed:", acceptedErr);
    return;
  }
  if (!acceptedRows || acceptedRows.length === 0) return;

  if (import.meta.env.DEV) {
    console.info("[useReconciliationConsumer] mirror sweep", {
      count: acceptedRows.length,
      limit,
    });
  }

  for (const row of acceptedRows as Array<{
    app_offer_id: string | null;
    app_job_id: string | null;
    contractor_id: string | null;
    decided_at: string | null;
    organization_id?: string | null;
  }>) {
    const scopedOrgId = row.organization_id || orgId;
    const respondedAt = row.decided_at ?? new Date().toISOString();

    if (row.app_offer_id && UUID_RE.test(row.app_offer_id)) {
      const { error } = await supabase
        .from("marketplace_offers")
        .update({
          status: "accepted",
          responded_at: respondedAt,
        })
        .eq("id", row.app_offer_id)
        .eq("organization_id", scopedOrgId)
        .eq("status", "pending");
      if (!error) continue;
      console.warn("[useReconciliationConsumer] mirror-by-id failed:", row.app_offer_id, error);
    }

    if (row.app_job_id && row.contractor_id) {
      const { error } = await supabase
        .from("marketplace_offers")
        .update({
          status: "accepted",
          responded_at: respondedAt,
        })
        .eq("job_id", row.app_job_id)
        .eq("contractor_id", row.contractor_id)
        .eq("organization_id", scopedOrgId)
        .eq("status", "pending");
      if (error) {
        console.warn("[useReconciliationConsumer] mirror-by-job+contractor failed:", row.app_job_id, error);
      }
    }
  }
}
